import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useRef, useState } from 'react';
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { Alert, Button, Typography } from '@/ui';

import {
  AssistantPageShell,
  AssistantSignOutButton,
} from './AssistantPageShell';
import { AssistantRequestLinkForm } from './AssistantRequestLinkForm';
import {
  assistantErrorCode,
  schedulingAssistantApi,
  useExchangeAssistantLinkMutation,
  useGetAssistedMembersQuery,
} from './api';
import { clearAssistantOperations } from './operationRecords';
import { assistantMemberPath } from './paths';
import { useAssistantSessionToken, writeAssistantSession } from './session';
import {
  ASSISTANT_HOME_PATH,
  useEndAssistantSessionOnUnauthorized,
  useSignOutAssistant,
} from './useAssistantSession';

import type { AssistantHomeLocationState } from './useAssistantSession';
import type { FC } from 'react';

import { useAppDispatch } from '@/store/hooks';

type Exchange =
  | { state: 'idle' }
  | { state: 'exchanging' }
  | { code: string | undefined; state: 'failed' };

export const linkFailureMessage = (code: string | undefined): string => {
  switch (code) {
    case 'link_used':
      return 'That sign-in link was already used. Request a new one below.';
    case 'link_expired':
      return 'That sign-in link has expired. Request a new one below.';
    case 'link_invalid':
      return 'That sign-in link isn’t valid. Request a new one below.';
    default:
      return 'We couldn’t sign you in with that link. Request a new one below.';
  }
};

const RequestLinkPanel: FC<{ notice?: string }> = ({ notice }) => (
  <div className="flex flex-col gap-4">
    <Typography variant="h4">Scheduling assistant sign-in</Typography>
    {notice ? <Alert severity="warning">{notice}</Alert> : null}
    <Typography variant="body1">
      Manage the Mento sessions of the person whose calendar you look after.
      Enter your email and we&apos;ll send you a sign-in link.
    </Typography>
    <AssistantRequestLinkForm />
  </div>
);

const AssistedMemberPicker: FC = () => {
  const token = useAssistantSessionToken();
  // Skipped once the session ends, so a cache reset never refetches it.
  const {
    currentData: cachedMembers,
    error,
    isError,
    isFetching,
    refetch,
  } = useGetAssistedMembersQuery(token ? undefined : skipToken);
  // A refetch means the list was invalidated (a member page got a 403): wait
  // for it rather than redirect to a member who removed the assistant.
  const members = isFetching ? undefined : cachedMembers;
  const { isSigningOut, signOut } = useSignOutAssistant();
  useEndAssistantSessionOnUnauthorized(error);

  const signOutButton = (
    <AssistantSignOutButton disabled={isSigningOut} onSignOut={signOut} />
  );

  const [onlyMember] = members ?? [];
  if (members?.length === 1 && onlyMember) {
    return <Navigate to={assistantMemberPath(onlyMember.memberId)} replace />;
  }

  if (isError) {
    return (
      <AssistantPageShell headerActions={signOutButton}>
        <div className="flex flex-col gap-4">
          <Typography variant="body1">
            We couldn&apos;t load who you assist.
          </Typography>
          <div>
            <Button variant="outlined" onClick={refetch}>
              Retry
            </Button>
          </div>
        </div>
      </AssistantPageShell>
    );
  }

  if (!members) {
    return (
      <AssistantPageShell headerActions={signOutButton}>
        <Typography variant="body1">Loading…</Typography>
      </AssistantPageShell>
    );
  }

  if (members.length === 0) {
    return (
      <AssistantPageShell headerActions={signOutButton}>
        <div className="flex flex-col gap-3">
          <Typography variant="h4">No one to assist right now</Typography>
          <Typography variant="body1">
            This email isn&apos;t an active scheduling assistant for anyone in
            Mento. Ask the person whose calendar you manage to add you again.
          </Typography>
        </div>
      </AssistantPageShell>
    );
  }

  return (
    <AssistantPageShell headerActions={signOutButton}>
      <div className="flex flex-col gap-4">
        <Typography variant="h4">Whose sessions?</Typography>
        <ul className="flex flex-col gap-2">
          {members.map((member) => (
            <li key={member.memberId}>
              <Link
                className="block rounded-xl border border-cement-300 px-4 py-3 text-cement-500 hover:bg-sage-100"
                to={assistantMemberPath(member.memberId)}
              >
                {member.displayName}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </AssistantPageShell>
  );
};

/**
 * `/assistant`: request a sign-in link, or arrive from one (`?token=`). The
 * token is taken out of the URL before it is exchanged, so it never stays in
 * history or a referrer; a failed exchange leaves the emailed link usable.
 */
const AssistantHome: FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const linkToken = searchParams.get('token');
  const exchangedToken = useRef<string | null>(null);
  const [exchange, setExchange] = useState<Exchange>(
    linkToken ? { state: 'exchanging' } : { state: 'idle' },
  );
  const [exchangeLink] = useExchangeAssistantLinkMutation();
  const sessionToken = useAssistantSessionToken();

  useEffect(() => {
    if (!linkToken || exchangedToken.current === linkToken) return;
    exchangedToken.current = linkToken;
    setExchange({ state: 'exchanging' });
    navigate(ASSISTANT_HOME_PATH, { replace: true });

    exchangeLink(linkToken)
      .unwrap()
      .then((grant) => {
        // A new link replaces whatever session this tab had, and may sign in
        // a different assistant. The old session's reschedule and cancel
        // records go first: one never answered would otherwise be replayed
        // under the new token, as a change this assistant never confirmed.
        clearAssistantOperations();
        dispatch(schedulingAssistantApi.util.resetApiState());
        writeAssistantSession(grant);
        setExchange({ state: 'idle' });
      })
      .catch((error: unknown) => {
        setExchange({ code: assistantErrorCode(error), state: 'failed' });
      });
  }, [dispatch, exchangeLink, linkToken, navigate]);

  if (exchange.state === 'exchanging') {
    return (
      <AssistantPageShell>
        <Typography variant="body1">Signing you in…</Typography>
      </AssistantPageShell>
    );
  }

  if (exchange.state === 'failed') {
    return (
      <AssistantPageShell>
        <RequestLinkPanel notice={linkFailureMessage(exchange.code)} />
      </AssistantPageShell>
    );
  }

  if (sessionToken) {
    return <AssistedMemberPicker />;
  }

  const expired = (location.state as AssistantHomeLocationState | null)
    ?.assistantSessionExpired;

  return (
    <AssistantPageShell>
      <RequestLinkPanel
        notice={expired ? 'Your link expired; request a new one.' : undefined}
      />
    </AssistantPageShell>
  );
};

export default AssistantHome;
